import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaUser,
  FaPhoneAlt,
  FaHome,
  FaMoneyBillWave,
  FaInfoCircle,
  FaShieldAlt,
} from "react-icons/fa";
import WarningModal from "../../components/WarningModal"; // ✅ default import
import { sendApplicationForm } from "../../services/emailService";

const ApplyNow = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [formData, setFormData] = useState({
    loanPurpose: "",
    loanAmount: "",
    mainReason: "",
    priority: "",
    securityWilling: false,
    firstName: "",
    lastName: "",
    dob: {
      day: "",
      month: "",
      year: "",
    },
    citizenship: "",
    incomeSource: "",
    creditScore: "",
    phone: "",
    email: "",
    address: {
      unitNumber: "",
      street: "",
      suburb: "",
      state: "",
      postcode: "",
    },
    agreeTerms: false,
    understandOffer: false,
    security: "",
    vehicleDetails: "",
    assetDescription: "",
    guarantorRelationship: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Show warning modal when component mounts
  useEffect(() => {
    setShowWarning(true);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

  setSubmitSuccess(false);
  setSubmitError(null);
  setIsSubmitting(true);

    try {
      setIsSubmitting(true);
      await sendApplicationForm(formData);
      setSubmitSuccess(true);

      // Reset form or redirect after successful submission
      setTimeout(() => {
        // Reset form or redirect
      }, 5000);
    } catch (error) {
      console.error("Error submitting application:", error);
      setSubmitError(
        "There was a problem submitting your application. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Warning Modal */}
      <WarningModal
        isOpen={showWarning}
        onClose={() => setShowWarning(false)}
      />

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            Apply for a Loan
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Complete the form below to apply for your personal loan. Our team
            will review your application and get back to you shortly.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="bg-white shadow-xl rounded-xl overflow-hidden"
        >
          <div className="bg-primary py-6 px-8">
            <h2 className="text-xl text-white font-semibold">
              Loan Application Form
            </h2>
            <p className="text-gray-200 text-sm">
              Please complete all required fields
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {/* Loan Details Section */}
            <div className="mb-10">
              <h3 className="text-xl font-semibold text-primary mb-6 flex items-center">
                <FaMoneyBillWave className="mr-2 text-secondary" /> Loan Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    What are you looking for?
                  </label>
                  <input
                    type="text"
                    name="loanPurpose"
                    value={formData.loanPurpose}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    placeholder="e.g., Home renovation, Car purchase..."
                    required
                  />
                </div> */}

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    How much would you like to borrow?
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">
                      $
                    </span>
                    <input
                      type="number"
                      name="loanAmount"
                      value={formData.loanAmount}
                      onChange={handleInputChange}
                      className="w-full pl-8 pr-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                      placeholder="Enter amount"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    What is your main reason for borrowing?
                  </label>
                  <select
                    name="mainReason"
                    value={formData.mainReason}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  >
                    <option value="">Please choose an option</option>
                    <option value="debt_consolidation">
                      Debt Consolidation
                    </option>
                    <option value="home_improvement">Home Improvement</option>
                    <option value="major_purchase">Major Purchase</option>
                    <option value="education">Education</option>
                    <option value="business">Business</option>
                    <option value="medical">Medical Expenses</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    What is most important to you?
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  >
                    <option value="">Please choose an option</option>
                    <option value="low_interest">Low Interest Rate</option>
                    <option value="low_repayment">Low Repayment</option>
                    <option value="quick_funding">Quick Funding</option>
                    <option value="flexible_terms">Flexible Terms</option>
                    <option value="customer_service">Customer Service</option>
                  </select>
                </div> */}
              </div>

            </div>

            {/* Personal Details Section */}
            <div className="mb-10">
              <h3 className="text-xl font-semibold text-primary mb-6 flex items-center">
                <FaUser className="mr-2 text-secondary" /> About You
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    First name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Last name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Date of Birth
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <select
                    name="dob.day"
                    value={formData.dob.day}
                    onChange={handleInputChange}
                    className="px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  >
                    <option value="">Day</option>
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>

                  <select
                    name="dob.month"
                    value={formData.dob.month}
                    onChange={handleInputChange}
                    className="px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  >
                    <option value="">Month</option>
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((month, i) => (
                      <option key={i} value={i + 1}>
                        {month}
                      </option>
                    ))}
                  </select>

                  <select
                    name="dob.year"
                    value={formData.dob.year}
                    onChange={handleInputChange}
                    className="px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  >
                    <option value="">Year</option>
                    {[...Array(80)].map((_, i) => {
                      const year = new Date().getFullYear() - 18 - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>

            {/* More About You Section */}
            <div className="mb-10">
              <h3 className="text-xl font-semibold text-primary mb-6 flex items-center">
                <FaInfoCircle className="mr-2 text-secondary" /> More About You
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    What is your residency status?
                  </label>
                  <select
                    name="citizenship"
                    value={formData.citizenship}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  >
                    <option value="">Please choose an option</option>
                    <option value="citizen">Australian Citizen</option>
                    <option value="permanent_resident">
                      Permanent Resident
                    </option>
                    <option value="temporary_resident">
                      Temporary Resident
                    </option>
                    <option value="other">International Student</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    What is your main source of income?
                  </label>
                  <select
                    name="incomeSource"
                    value={formData.incomeSource}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  >
                    <option value="">Please choose an option</option>
                    <option value="full_time">Full-time Employment</option>
                    <option value="part_time">Part-time Employment</option>
                    <option value="self_employed">Casual Employment</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    What is your marital status?
                  </label>
                  <select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  >
                    <option value="">Please choose an option</option>
                    <option value="single">Single</option>
                    <option value="defacto">De Facto</option>
                    <option value="married">Married</option>
                    <option value="separated">Separated</option>
                    <option value="others">Others</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    How many dependents do you have?
                  </label>
                  <input
                    type="number"
                    name="dependents"
                    value={formData.dependents}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    placeholder="Enter number of dependents"
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    How would you describe your current credit score?
                  </label>
                  <select
                    name="creditScore"
                    value={formData.creditScore}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  >
                    <option value="">Please choose an option</option>
                    <option value="excellent">Excellent (800+)</option>
                    <option value="very_good">Very Good (700-799)</option>
                    <option value="good">Good (650-699)</option>
                    <option value="fair">Fair (550-649)</option>
                    <option value="poor">Poor (below 550)</option>
                    <option value="not_sure">Not Sure</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Details Section */}
            <div className="mb-10">
              <h3 className="text-xl font-semibold text-primary mb-6 flex items-center">
                <FaPhoneAlt className="mr-2 text-secondary" /> Your Contact
                Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Mobile
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    placeholder="0400 000 000"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="mb-10">
              <h3 className="text-xl font-semibold text-primary mb-6 flex items-center">
                <FaHome className="mr-2 text-secondary" /> Your Address
              </h3>

              <div className="grid grid-cols-1 gap-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Unit/Suite Number
                    </label>
                    <input
                      type="text"
                      name="address.unitNumber"
                      value={formData.address.unitNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Suburb
                    </label>
                    <input
                      type="text"
                      name="address.suburb"
                      value={formData.address.suburb}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      State
                    </label>
                    <select
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                      required
                    >
                      <option value="">Please Select</option>
                      <option value="NSW">New South Wales</option>
                      <option value="VIC">Victoria</option>
                      <option value="QLD">Queensland</option>
                      <option value="WA">Western Australia</option>
                      <option value="SA">South Australia</option>
                      <option value="TAS">Tasmania</option>
                      <option value="ACT">Australian Capital Territory</option>
                      <option value="NT">Northern Territory</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Postcode
                    </label>
                    <input
                      type="text"
                      name="address.postcode"
                      value={formData.address.postcode}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                      maxLength="4"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Security Information Section */}
            <div className="mb-10">
              <h3 className="text-xl font-semibold text-primary mb-6 flex items-center">
                <FaShieldAlt className="mr-2 text-secondary" /> Security
                Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                  Proving security may increase chance of funding and improve offers from lenders. Are you willing and able to provide some form of security as part of your application?
                  </label>
                  <div className="relative">
                    <select
                      name="security"
                      value={formData.security || ""}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300 pr-10"
                      required
                    >
                      <option value="" disabled>Please choose an option</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                    {(formData.security === "yes" || formData.security === "no") && (
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500 text-xl">✔️</span>
                    )}
                  </div>
                </div>

                

                {formData.security === "vehicle" && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Vehicle Details
                    </label>
                    <input
                      type="text"
                      name="vehicleDetails"
                      value={formData.vehicleDetails || ""}
                      onChange={handleInputChange}
                      placeholder="Year, Make, Model"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    />
                  </div>
                )}

                {formData.security === "other_asset" && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Asset Description
                    </label>
                    <input
                      type="text"
                      name="assetDescription"
                      value={formData.assetDescription || ""}
                      onChange={handleInputChange}
                      placeholder="Describe your asset"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    />
                  </div>
                )}

                {formData.security === "guarantor" && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Guarantor Relationship
                    </label>
                    <input
                      type="text"
                      name="guarantorRelationship"
                      value={formData.guarantorRelationship || ""}
                      onChange={handleInputChange}
                      placeholder="e.g. Parent, Sibling, Friend"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 transition duration-300"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-8 bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="space-y-4">
<label className="flex items-start">
  <input
    type="checkbox"
    name="agreeTerms"
    checked={formData.agreeTerms}
    onChange={handleInputChange}
    className="mt-1 h-4 w-4 text-secondary focus:ring-secondary border-gray-300 rounded"
    required
  />
  <span className="ml-2 text-gray-700">
    I confirm that the information I have provided is true and accurate, that I am authorised to provide the personal details presented, and that I consent to my information being checked with the document issuer or official record holder via third party systems to confirm my identity as described in the{" "}
    <a
      href="/legal/privacy-statement"
      className="text-secondary underline hover:text-primary"
    >
      Privacy Policy
    </a>.
  </span>
</label>


                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="understandOffer"
                    checked={formData.understandOffer}
                    onChange={handleInputChange}
                    className="mt-1 h-4 w-4 text-secondary focus:ring-secondary border-gray-300 rounded"
                    required
                  />
                  <span className="ml-2 text-gray-700">
                    I also understand I may be offered a financial product from
                    another lender, and based on their credit assessment of my
                    application, it may not be the exact same type or amount I
                    applied for.
                  </span>
                </label>
              </div>
            </div>

            {/* Submit Button */}
{/* Submission feedback */}
{submitSuccess && (
  <div className="mb-6 p-4 bg-green-100 border border-green-300 text-green-800 rounded-lg">
    Your application was submitted successfully. We will contact you shortly.
  </div>
)}

{submitError && (
  <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-800 rounded-lg">
    {submitError}
  </div>
)}

{/* Submit Button */}
<div className="text-center">
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    type="submit"
    disabled={isSubmitting}
    className="px-8 py-4 bg-secondary hover:bg-green-600 text-white font-medium text-lg rounded-lg transition duration-300 shadow-md"
  >
    {isSubmitting ? "Submitting..." : "Submit Application"}
  </motion.button>

              <p className="text-gray-500 mt-4 text-sm">
                Your information is secure and encrypted
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ApplyNow;
